import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAgendaEvent,
  getAlarmsQueue,
  getAlarmsQueueLastUpdate,
  getAlertCandidates,
  getDevicesByHwSerialNo,
  getMeasure, getOperatorGroups,
  getPatient,
  getPatientAgendaEvents,
  postCallAlarm,
  postCloseAlarm,
  postConsumeAlarm,
  postDeafMemberAlarm,
  postJoinExternal,
  postLeaveMemberAlarm,
  postMuteMemberAlarm,
  postParkAlarm,
  postPlayDtmf,
  postSelectAlertClient,
  postUndeafMemberAlarm,
  postUnmuteMemberAlarm,
  putOperatorGroups
} from "../../services/api";

import {
  DEFAULT_ALARM_QUEUE_SIZE,
  clearContextData,
  resetAlarm,
  resetAlertCandidates,
  setAlarmSelected,
  setAutoAssignmentFromCall,
  setManualFromMyAlerts,
  setWorkingAlerts,
  showAlarmResolution,
  showClientSelection,
  toggleAlarmQueue
} from "./alarmQueueSlice";
import { getPatientLastLocationAsync } from '../userDetail/asyncActions';
import { selectIsInstaller } from '../common/commonSelectors';
import { needsClientIdentification } from './alarmQueueUtils';
import { callsDisabledFor } from './usePbxStatus';
import { expedientIdForCall } from '../expedientQueue/expedientQueueUtils';
import { pushNotification } from '../notifications/notificationsSlice';
import { i18n } from 'utils';

// SAC credentials are injected by the apiSac request interceptor — thunks
// only need to provide operatorId / payload data.

export const getAlarmQueueAsync = createAsyncThunk('alarmQueue/getAlarmQueue', async (_, { getState }) => {
  const state: any = getState();
  const { id: operatorId } = state.common.user.userLogged;
  const size = state.alarmQueue?.alarmRequestSize ?? DEFAULT_ALARM_QUEUE_SIZE;
  const order = state.alarmQueue?.alarmsQueueOrder ?? 'desc';

  const response = await getAlarmsQueue(operatorId, size, order);

  const data = response?.data ?? {};

  return {
    items: data.alerts ?? [],
    myItems: data.operator?.workingAlerts ?? [],
    totalSize: data.totalSize ?? 0,
    lastUpdate: data.lastUpdateTimestamp ?? null,
    operatorAlert: data.operator.alert ?? null,
    // El tamaño con el que se lanzó ESTA petición: el reducer lo usa para
    // descartar respuestas obsoletas y detectar el final de la cola.
    requestedSize: size,
  };
});

export const getAlarmQueueLastUpdateAsync: any | null = createAsyncThunk(
  'alarmQueue/getAlarmQueueLastUpdate',
  async (_, { getState }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    const response = await getAlarmsQueueLastUpdate(operatorId);

    const data = response?.data ?? {};

    return {
      lastUpdate: data.lastUpdateTimestamp ?? null,
      // Estado de conexion con la centralita (UP/RECOVERING/DOWN) para el banner.
      pbxStatus: data.pbxStatus ?? null,
    };
  },
);

export const loadAlarmContextData = createAsyncThunk(
  'alarmQueue/loadAlarmContextData',
  async (alarm: any, { dispatch, getState }) => {

    if (!alarm) return;

    const state: any = getState();
    const measureStatus = state.masters.measureStatus;
    const measuresGenericTypes = state.masters.measuresGenericTypes;

    // --- Patient Base Context ---
    if (alarm?.client?.uuid) {
      dispatch(getPatientAlarmAsync(alarm.client.uuid));
    }

    // --- Specific alarm type detail ---
    if (alarm?.reference) {

      // MEASURE
      if (alarm.type === 'MEASURE_OUT_OF_RANGE' || alarm.type === 'MeasureOutOfRange') {
        dispatch(
          getMeasureAlarmAsync({
            patientId: alarm.client.uuid,
            measureRawId: alarm.reference,
            measureStatus,
            measuresGenericTypes,
          }),
        );
      }

      // AGENDA
      if (alarm.type === 'Agenda') {
        dispatch(getAgendaAlarmAsync({ eventId: alarm.reference }));
      }
    }

    // LOCATION (VOICE 'A' alarms). gdp-api no se la abre al instalador (ver INSTALLER_ACCESS).
    if (alarm.type === 'A' && !selectIsInstaller(state)) {
      dispatch(getPatientLastLocationAsync(alarm.client.uuid));
    }

    // TECH ALARM (device failure)
    if (alarm.type === 'TECH') {
      dispatch(getDevicesAlarmAsync({ deviceHwSerial: alarm.deviceId }));
    }
  },
);

export const postConsumeAlarmAsync = createAsyncThunk(
  'alarmQueue/consumeAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    const response: any = await postConsumeAlarm(operatorId, data.alarm.uuid);

    if (response?.data?.ok) {
      dispatch(clearContextData());
      dispatch(setAlarmSelected({ ...data.alarm, alarmTypeSelected: data.cardType }));
      dispatch(toggleAlarmQueue(true));

      if (needsClientIdentification(data.alarm)) {
        // Numero ambiguo: el operador debe elegir el paciente antes de ver datos.
        dispatch(resetAlertCandidates());
        dispatch(getAlertCandidatesAsync(data.alarm.uuid));
        dispatch(showAlarmResolution(false));
        dispatch(showClientSelection(true));
      } else {
        dispatch(loadAlarmContextData(data.alarm));
        dispatch(showAlarmResolution(true));
      }
    } else {
      // Cambio manual entre mis alertas
      dispatch(setManualFromMyAlerts(true));

      // Refrescamos la cola para obtener la actualizada
      const refreshed = await dispatch(getAlarmQueueAsync()).unwrap();
      const updatedAlert = refreshed?.operatorAlert;

      if (updatedAlert) {
        dispatch(clearContextData());
        dispatch(setAlarmSelected({ ...updatedAlert, alarmTypeSelected: data.cardType }));
        dispatch(loadAlarmContextData(updatedAlert));
      }
    }

    return { ok: response?.data?.ok, alarm: data.alarm };
  },
);

export const getAlertCandidatesAsync = createAsyncThunk(
  'alarmQueue/getAlertCandidates',
  async (alarmId: any) => {
    const response = await getAlertCandidates(alarmId);
    return response?.data?.candidates ?? [];
  },
);

export const postSelectAlertClientAsync = createAsyncThunk(
  'alarmQueue/selectAlertClient',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    const response: any = await postSelectAlertClient(operatorId, data.alarmId, data.clientId);

    if (response?.data?.ok) {
      // Tras elegir, la alerta queda reatada al paciente real. Refrescamos la
      // cola para recuperarla actualizada y continuar el flujo normal.
      const refreshed = await dispatch(getAlarmQueueAsync()).unwrap();
      const updatedAlarm =
        [...(refreshed?.items ?? []), ...(refreshed?.myItems ?? [])].find((a: any) => a.uuid === data.alarmId) ??
        refreshed?.operatorAlert ??
        null;

      dispatch(resetAlertCandidates());
      dispatch(showClientSelection(false));

      if (updatedAlarm) {
        dispatch(clearContextData());
        dispatch(setAlarmSelected({ ...updatedAlarm, alarmTypeSelected: data.cardType }));
        dispatch(loadAlarmContextData(updatedAlarm));
        dispatch(showAlarmResolution(true));
      }
    }

    return response;
  },
);

/** Motivos del SAC para no atar una llamada a un expediente (DoCallService.rejectExpedient). */
const CALL_EXPEDIENT_REJECTIONS = ['ExpedientNotFound', 'ExpedientOtherClient', 'ExpedientHeldByOther'];

export const postCallAlarmAsync = createAsyncThunk(
  'alarmQueue/postCallAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();

    // Sin centralita utilizable no se pueden cursar salientes (fallarian).
    // Guard central: cubre cualquier boton/vía que llame a este thunk.
    const pbxStatus = state.alarmQueue?.pbxStatus;
    if (callsDisabledFor(pbxStatus)) {
      throw new Error('PBX_UNAVAILABLE');
    }

    const { id: operatorId } = state.common.user.userLogged;

    // Llamada atada a un expediente (llamadas-desde-expediente); sin él, el body es el de siempre.
    const expedientId = expedientIdForCall(data.expedientId, data.userId, state.expedientQueue?.selectedExpedient);

    const body = {
      operatorId,
      target: data.target,
      type: data.type,
      reference: null,
      ...(expedientId ? { expedientId } : {}),
    };

    let params = {};

    if (data.userId) {
      params = {
        clientId: data.userId,
      };
    }

    dispatch(resetAlarm());
    dispatch(clearContextData());
    dispatch(setAutoAssignmentFromCall(true));

    const response = await postCallAlarm(body, params);

    // El SAC rechaza ANTES de marcar si el expediente no vale (otro operador lo atiende, ya no existe…).
    const rejection = response?.data?.ok === false ? response?.data?.messageError : null;
    if (rejection && CALL_EXPEDIENT_REJECTIONS.includes(rejection)) {
      dispatch(setAutoAssignmentFromCall(false));
      dispatch(pushNotification({ message: i18n(`expedient.call.error.${rejection}`), severity: 'warning' }));
    }

    return handleCallAlarmResponse(response, dispatch);
  },
);

async function handleCallAlarmResponse(response: any, dispatch: any) {
  if (response?.data?.ok) {
    const alert = response.data.alert;

    if (alert) {
      dispatch(setAlarmSelected(alert));
      dispatch(toggleAlarmQueue(true));
    } else {
      // fallback opcional: forzar una actualización manual de la cola
      dispatch(getAlarmQueueAsync());
    }
  }

  return response;
}

export const postCloseAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/postCloseAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    // El close devuelve operator.workingAlerts (el estado del operador tras
    // colgar). Lo aplicamos al store al instante para que Mute/Colgar y la
    // detección de "llamada viva" reaccionen sin esperar al siguiente poll.
    const applyWorkingAlerts = (response: any) => {
      const workingAlerts = response?.data?.operator?.workingAlerts;
      if (workingAlerts) dispatch(setWorkingAlerts(workingAlerts));
    };

    // `silent`: colgar/cerrar como paso best-effort (p.ej. tras resolver). SAC
    // devuelve error si la alarma ya no existe (validateAlertDomain null →
    // NotAllowException), lo que ocurre cuando la llamada ya había terminado o
    // se colgó con CallEnd antes. En ese caso NO queremos un toast de error:
    // resolvemos el thunk en vez de rechazarlo, así el middleware no lo muestra.
    // El botón CallEnd sigue llamando sin `silent`, conservando su feedback.
    if (data.silent) {
      try {
        const response = await postCloseAlarm(operatorId, data.alarmId);
        applyWorkingAlerts(response);
        return response;
      } catch (err) {
        return { data: { ok: false, silentError: true } };
      }
    }
    const response = await postCloseAlarm(operatorId, data.alarmId);
    applyWorkingAlerts(response);
    return response;
  },
);

export const postLeaveMemberAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/postLeaveMemberAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postLeaveMemberAlarm(operatorId, data.alarmId, data.number);
    response?.data.ok && dispatch(getAlarmQueueAsync());
    return response;
  },
);

export const postMuteMemberAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/postMuteMemberAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postMuteMemberAlarm(operatorId, data.alarmId, data.number);
    response?.data.ok && dispatch(getAlarmQueueAsync());
    return response;
  },
);

export const postUnmuteMemberAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/postUnmuteMemberAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postUnmuteMemberAlarm(operatorId, data.alarmId, data.number);
    response?.data.ok && dispatch(getAlarmQueueAsync());
    return response;
  },
);

export const postDeafMemberAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/postDeafMemberAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postDeafMemberAlarm(operatorId, data.alarmId, data.number);
    response?.data.ok && dispatch(getAlarmQueueAsync());
    return response;
  },
);

export const postUndeafMemberAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/postUndeafMemberAlarm',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postUndeafMemberAlarm(operatorId, data.alarmId, data.number);
    response?.data.ok && dispatch(getAlarmQueueAsync());
    return response;
  },
);

export const postParkAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/postParkAlarm',
  async (data: any, { getState }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postParkAlarm(operatorId, data.alarmId);
    return response;
  },
);

export const postPlayDtmfAsync: any | null = createAsyncThunk(
  'alarmQueue/postPlayDtmf',
  async (data: any, { getState }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postPlayDtmf(operatorId, data.alarmId, data.number, data.dtmf);
    return response;
  },
);

export const postPostJoinExternalAsync: any | null = createAsyncThunk(
  'alarmQueue/postJoinExternal',
  async (data: any, { getState }) => {
    const state: any = getState();

    // Meter un numero externo en la conferencia es una saliente: sin centralita falla.
    const pbxStatus = state.alarmQueue?.pbxStatus;
    if (callsDisabledFor(pbxStatus)) {
      throw new Error('PBX_UNAVAILABLE');
    }

    const operatorId = state.common.user.userLogged.id;
    const response = await postJoinExternal(operatorId, data.alarmId, data.externalNumber, data.clientId);
    return response;
  },
);

export const getPatientAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/getPatientAlarm',
  async (data: any) => {
    const response = await getPatient(data);
    return response;
  },
);

export const getMeasureAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/getMeasure',
  async (data: any, { getState }) => {
    const state: any = getState();
    const response = await getMeasure(data);
    const measuresTypes: any[] = state.masters?.measuresTypes?.data ?? [];
    const measureTypeLookup: Record<string, string> = Object.fromEntries(
      measuresTypes.map((t: any) => [t.code, t.label ?? t.code]),
    );
    return { response, measureTypeLookup };
  },
);

export const getDevicesAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/getDevices',
  async (data: any) => {
    const response = await getDevicesByHwSerialNo(data);
    return response;
  },
);

export const getAgendaAlarmAsync: any | null = createAsyncThunk(
  'alarmQueue/getAgenda',
  async (data: any) => {
    const response = await getAgendaEvent(data);

    if (response?.status === 200) {
      const eventsResponse = await getPatientAgendaEvents({
        activityId: response.data.data.activity.id,
        onlyFutureEvents: 'true',
      });
      response.data.data.futureEvents = eventsResponse?.data.data;
    }

    return response;
  },
);

export const getOperatorGroupsAsync: any | null = createAsyncThunk(
  'alarmQueue/getOperatorGroups',
  async (data: any, { getState }) => {
    const state: any = getState();
    const operatorId = data?.operatorId ?? state.common.user.userLogged.id;
    const response = await getOperatorGroups(operatorId);
    return response;
  },
);

export const putOperatorGroupsAsync: any | null = createAsyncThunk(
  'alarmQueue/putOperatorGroups',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const operatorId = data?.operatorId ?? state.common.user.userLogged.id;
    const response = await putOperatorGroups(operatorId, { operatorGroups: data?.operatorGroups });

    if (response?.status === 200) {
      dispatch(getOperatorGroupsAsync({ operatorId }));
      dispatch(getAlarmQueueAsync());
    }

    return response;
  },
);
